<template>
    <form id="payment-form"
          :data-username="user.username"
          class="main"
          method="POST"
          @submit.prevent="saveStepThree()"
          :action="`/registration/${user.username}`"
          enctype="multipart/form-data">


        <!-- Step 1 -->
        <div class="step step--1 step--centered wrapper">
            <div class="step__inner">

                    <div class="confetti-intro on-vue">
                        <div class="icon-wrap">
                            <confetti :trigger-count="3"></confetti>
                            <svg class="icon-star"><use xlink:href="#icon-star"></use></svg>
                        </div>
                        <h2>Welcome to the Club</h2>
                        <p>
                            You're one step closer to joining our community of travelers, foodies and adventure seekers (yay!).
                            We just need a few quick details and then you’ll be on your way.
                            <br><br>
                            <strong>Account Set Up:</strong>
                            <br>
                            Please create a username and password below.
                        </p>
                    </div>

                <div class="form">
                    <div class="form__row">

                        <!-- Username -->
                        <div class="form__field form__field--username form__field--mobile-full" :class="{'has-error' : form.errors.has('username')}">
                            <label for="username"  class="accessible">Username</label>
                            <input type="text"
                                   id="username"
                                   autocapitalize="off"
                                   name="username"
                                   v-model="form.username"
                                   placeholder="Create Username"
                                   class="username required">
                            <span v-if="form.errors.has('username')" class="error" v-text="form.errors.username"></span>
                        </div>

                        <!-- Password -->
                        <div class="form__field form__field--password form__field--mobile-full" :class="{'has-error' : form.errors.has('password')}">
                            <label for="password" class="accessible">Password</label>
                            <input type="password"
                                   id="password"
                                   name="password"
                                   v-model="form.password"
                                   placeholder="Create Password"
                                   class="required">
                            <span v-if="form.errors.has('password')" class="error" v-text="form.errors.password"></span>
                        </div>

                        <!-- Password Confirmation -->
                        <div class="form__field form__field--confirm-password form__field--mobile-full"
                             :class="{'has-error' : form.errors.has('password_confirmation')}">
                            <label for="confirm_password" class="accessible">Confirm Password</label>
                            <input type="password"
                                   id="confirm_password"
                                   name="confirm_password"
                                   v-model="form.password_confirmation"
                                   placeholder="Confirm Password"
                                   class="required confirm-pass">
                            <span v-if="form.errors.has('password_confirmation')" class="error" v-text="form.errors.password_confirmation"></span>
                        </div>
                    </div>

                    <!-- Next Step Button -->
                    <div class="form__button">
                        <button type="button"
                                @click="saveStepOne()"
                                class="button"
                                :class="{'disabled': stepOneDisabled }"
                                :disabled="form.isSubmitted"
                                tabindex="0">
                            Next Step
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>

                </div>

            </div>
        </div>

        <!-- Step 2 -->
        <div class="step step--2 wrapper">
            <div class="step__inner">
                <div class="form">

                    <!-- Photo -->
                    <div class="form__row" aria-labelledby="profile_picture_edit" role="group">

                        <div class="form__row__left">
                            <label for="social-media">
                                Profile Photo*
                                <span>You can change <br/>this at any time</span>
                            </label>
                        </div>

                        <div class="form__row__right js-image-uploader" image-type="profile_picture" upload-type="ajax">
                            <div class="account-image">
                                <croppie v-model="form.profile_picture"
                                         label="Please confirm your profile photo"
                                         :errors="form.errors.profile_picture">
                                </croppie>
                            </div>
                        </div>
                    </div>

                    <!-- Phone -->
                    <div class="form__row">
                        <div class="form__row__left">
                            <label for="phone_number">Cell Number</label>
                        </div>

                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('phone_number') }">
                                <international-phone id="phone_number"
                                                     @validation="isValidPhoneNumber = $event"
                                                     name="phone_number"
                                                     v-model="form.phone_number"
                                                     placeholder="Enter Phone Number">
                                </international-phone>

                                <span class="error" v-text="form.errors.phone_number"></span>
                            </div>
                        </div>
                    </div>

                    <!-- Bio -->
                    <div class="form__row">

                        <div class="form__row__left">
                            <label for="bio">
                                Your Bio
                            </label>
                        </div>

                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('biography')}">
                                <textarea id="bio"
                                          name="biography"
                                          v-model="form.biography"
                                          rows="5"
                                          placeholder="ie: I work in tech marketing. I’m an avid skier, ameteur chef and professional wine drinker. A few of my favorite travel destinations include Zermatt, Tulum and Santorini.">
                                </textarea>
                                <span v-if="form.errors.has('biography')" class="error" v-text="form.errors.biography"></span>
                            </div>
                        </div>
                    </div>

                    <!-- Button Next -->
                    <div class="form__button">
                        <button class="button"
                                :class="{'disabled': stepTwoDisabled }"
                                :disabled="form.isSubmitted"
                                @click="saveStepTwo"
                                type="button"
                                tabindex="0">
                            Next Step
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>

                </div>
            </div>
        </div>

        <!-- Step 3 -->
        <div class="step step--3 step--payment wrapper">
            <div class="step__inner">
                <div class="form">

                    <div class="step--payment__header">
                        <h2>Start Your Free Trial</h2>
                        <p>You're just steps away from Club status. Scared of commitment? We get it. Good news is you get a 14-day free trial to check things out, worry free (spoiler alert, we think you're going to love it).</p>
                    </div>

                    <!-- Membership Information -->
                    <div class="step--payment__body">

                        <div>
                            <h3>Your Well Traveled membership includes</h3>
                            <membership-information></membership-information>
                        </div>

                        <p v-if="bookingCredit" class="bonus-perk">
                            Bonus Perk: $<span v-text="bookingCredit"></span> off your first trip booked with WT
                        </p>
                    </div>

                    <!-- Payment Details -->
                    <div class='step--payment__details'>
                        <h3>Your Membership Details</h3>
                        <ul>
                            <li>Membership plan <span>{{ membershipInformation.price_text }}/year</span></li>
                            <li class='promo' v-if="couponApplied">
                                <span class="promo-name" v-if="couponApplied.description" v-text="couponApplied.description"></span>
                                <span v-else>Promo Applied</span>
                                <span class="promo-cost" v-if="couponApplied.cost" v-text="couponApplied.cost"></span>
                            </li>
                            <li class="trial-end">Free trial Ends <span v-text="trialEndDateFromNow()"></span></li>
                            <li class='final'>Total due today <span>$0</span></li>
                        </ul>
                    </div>

                    <!-- Apply Promo Code -->
                    <div class="form__row promo-code-wrap">
                        <div class="form__row__right">
                            <div class="form__field form__field--no-wrap">
                                <input name="promo_code" id="promo_code" v-model="couponForm.code" type="text" placeholder="Promo Code">
                                <button type="button" class="promo-button" @click="addPromo()">
                                    <span v-if="couponApplied">Applied</span>
                                    <span v-else>Apply</span>
                                </button>
                            </div>
                            <span class="promo-error" v-text="couponForm.errors.code"></span>
                            <span class="promo-error" v-text="form.errors.coupon_id"></span>
                        </div>
                    </div>

                    <!-- Promo Message and Remove Promo button -->
                    <div class="form__row promo-code-final-row">
                        <div class="form__row__right"v-if="couponApplied">
                            <span class="promo-name">
                                Promo Applied
                                <span v-if="couponApplied.description" v-text="`: ${couponApplied.description}`"></span>
                            </span>
                            <span class="remove-promo" @click="removePromo()">
                                <svg class="close"><use xlink:href="#close"></use></svg>
                            </span>
                        </div>
                    </div>

                    <!-- Payment Form -->
                    <div class='step--payment__form'>

                        <h3>Billing Info</h3>

                        <div class="credit-card-tabs">

                            <!-- Credit Card -->
                            <span class="credit-card-tab"
                                  :class="{'active' : paymentOptionSelected == 'creditCard'}"
                                  @click="setPaymentOption('creditCard')"
                                  data-tar="reg-main-cc">
                                <svg class="cc-mc"><use xlink:href="#cc-mc"></use></svg>
                                <svg class="cc-ae"><use xlink:href="#cc-ae"></use></svg>
                                <svg class="cc-vi"><use xlink:href="#cc-vi"></use></svg>
                                <span>Credit&nbsp;</span> Card
                            </span>
                            <!-- Apple Pay -->
                            <span class="credit-card-tab apple-pay"
                                  :class="{'active' : paymentOptionSelected == 'applePay'}"
                                  @click="setPaymentOption('applePay')"
                                  data-tar="reg-alt-cc"
                                  v-if="stripePaymentOptions.applePay">
                                <img src="/images/apple.png" /> Apple Pay
                            </span>
                            <!-- Google Pay -->
                            <span class="credit-card-tab google-pay"
                                  :class="{'active' : paymentOptionSelected == 'googlePay'}"
                                  @click="setPaymentOption('googlePay')"
                                  data-tar="reg-alt-cc"
                                  v-if="stripePaymentOptions.googlePay">
                                <img src="/images/google.png" /> Google Pay
                            </span>
                            <!-- Link -->
<!--                            <span class="credit-card-tab link-pay"-->
<!--                                  :class="{'active' : paymentOptionSelected == 'link'}"-->
<!--                                  @click="setPaymentOption('link')"-->
<!--                                  data-tar="reg-alt-cc"-->
<!--                                  v-if="stripePaymentOptions.link">-->
<!--                                <img src="/images/stripe.png" /> Link-->
<!--                            </span>-->
                        </div>

                        <div class='credit-card-options'>

                            <!-- Start Free Trial Button for Credit Card -->
                            <div class="form__section"
                                 :class="{'active': paymentOptionSelected == 'creditCard'}"
                                 id="reg-main-cc">
                                <div class="form__row">
                                    <div class="form__row__right">
                                        <div id="stripe-card-element" class="stripe-card-element"></div>
                                        <span class="error" v-text="form.errors.stripe_token"></span>
                                        <span id="card-errors" style="margin-bottom: 20px" role="alert"></span>

                                        <button type="submit"
                                                class="button start-trial"
                                                :class="{ 'disabled' : stepThreeDisabled }"
                                                :disabled="form.isSubmitted">

                                            Start My Free Trial

                                            <svg class="arrow-right-2">
                                                <use xlink:href="#arrow-right-2"></use>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Apple and Google Pay Start Free Trial -->
                            <div class="form__section"
                                 :class="{'active': ['applePay', 'googlePay'].includes(paymentOptionSelected) }"
                                 id="reg-alt-cc">
                                <div class="form__row">
                                    <div class="form__row__right">
                                        <div id="payment-request-button">
                                            <!-- A Stripe Apple and Google Pay will be inserted here. -->
                                        </div>
                                        <button class="button disabled start-trial">
                                            Start My Free Trial
                                            <svg class="arrow-right-2">
                                                <use xlink:href="#arrow-right-2"></use>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <billing-terms></billing-terms>
                    </div>

                    <!-- Testimonials -->
                    <div class='step--payment__testimonial'>
                        <h4>What members are saying:</h4>
                        <blockquote>
                            "I loved my experience using WT to plan my trip to Turks & Caicos. I used Well Traveled recs for every meal on the island. Breakfast at Lemon 2 Go, lunch at Cocovan Airstream Lounge, followed by dinner at Infiniti Bar. All three places were amazing!"
                        </blockquote>
                        <div class="person">
                            <img
                                src="https://www.welltraveledclub.com/images/app/hTyaRVoztdEGfD2o9ak1urcS4KzlG2CCMIqdPi0l.jpg">
                            <h3>Ben Johnson</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </form>
</template>
<script>

import InternationalPhone from '@/components/InternationalPhone'
import Helpers from '@/mixins/Helpers'
import MultiStep from "@/mixins/MultiStep"
import StripeElements from "@/mixins/StripeElements"
import Confetti from "@/components/Confetti"
import Croppie from "@/components/Croppie"
import BillingTerms from "@/components/Pages/BillingTerms"
import MembershipInformation from "@/components/Pages/MembershipInformation"
import Form from "@/classes/Form"
import axios from '@/util/axios'
import byteSize from 'byte-size'

export default {

    mixins: [MultiStep, StripeElements, Helpers],

    props:['user', 'regFunnel', 'coupon', 'bookingCredit'],

    components:{
        Croppie,
        Confetti,
        BillingTerms,
        InternationalPhone,
        MembershipInformation,
    },

    data(){
        return {

            paymentOptionSelected: 'creditCard',
            isValidPhoneNumber: false,
            totalSteps: 3,
            allowScrollIn: [3],
            form: new Form({
                user_id: this.user.id,
                // Base
                step: "Welcome",
                user_id: this.user.id,
                reg_funnel_id: this.regFunnel ? this.regFunnel.id : null,
                // Step 1
                username: config.debug ? this.user.username : '',
                password: config.debug ? 'password' : '',
                password_confirmation: config.debug ? 'password' : '',
                // Step 2
                profile_picture: '',
                phone_number: this.user.user_info ? (this.user.user_info.phone_number ? this.user.user_info.phone_number : '') : '',
                biography: '',
                // Step 3
                coupon_id: '',
                stripe_token: '',
            })
        }
    },

    created() {
        // this.stripeStyle.base.backgroundColor = 'E8E0CC';
        this.addAbandonedListener()
        this.handleEnterKey()
        if (this.coupon) {
            this.couponForm.code = this.coupon.coupon
            this.addPromo()
        }
        eventBus.$on('step-change', this.saveAbandoned)
    },

    methods: {

        addAbandonedListener(){
            window.addEventListener('beforeunload', this.submitMixPanelAbandoned, {capture: true});
        },

        removeAbandonedListener(){
            window.removeEventListener('beforeunload', this.submitMixPanelAbandoned, {capture: true})
        },

        saveAbandoned(){
            this.form.patch(`/abandoned-funnel/application`, {
                clearForm: false
            })
        },

        submitMixPanelAbandoned(event){
            event.preventDefault();
            this.saveAbandoned()
            this.form.patch(`/abandoned-funnel/application-mixpanel`, {
                clearForm: false
            })
        },

        // Save the step 1 and move forward.
        saveStepOne() {
            let fields = _.pick(this.form, [
                'step',
                'username',
                'password',
                'password_confirmation',
            ]);
            this.validate(fields).then(response => {
                this.form.step = 'Your Details'
                this.setStepCompleted(1)
                this.changeStep(2)
            })
        },

        // Save the step 2 and move forward.
        saveStepTwo() {
            const formData = new FormData()
            formData.append('profile_picture', this.form.profile_picture)
            formData.append('biography', this.form.biography)
            formData.append('phone_number', this.form.phone_number)
            this.validate(formData)
                .then(response => {
                    if (this.form.phone_number.trim() != "" && !this.isValidPhoneNumber) {
                        this.form.errors.add("phone_number", "Please enter a valid phone number.")
                        return
                    }
                    if (byteSize(this.form.profile_picture.size).value >= 10000) {
                        this.form.errors.add("profile_picture", "The profile picture may not be greater than 5MB.")
                        return
                    }
                    this.form.step = 'Billing'
                    this.setStepCompleted(2)
                    this.changeStep(3)
                })
        },

        // Save the step 3 and move forward.
        saveStepThree() {
            let fields = _.pick(this.form, [
                'step',
                // 'stripe_token', Stripe token can not be validated for the step three.
                'coupon_id',
            ]);
            this.validate(fields).then(response => {
                this.setStepCompleted(3)
                this.submitPaymentIntent()
            })
        },

        stripeTokenHandler(stripeToken) {
            eventBus.$emit('start-processing-payment-dialog')
            this.form.stripe_token = stripeToken
            this.submitRegistration()
        },

        couponAddedHandler(coupon) {
            this.form.coupon_id = coupon.id
        },

        couponRemovedHandler() {
            this.form.coupon_id = ''
            this.couponForm.code = ''
        },

        // Make a request to validate the data.
        async validate(data) {
            return new Promise((resolve, rejected) => {
                this.form.errors.clear()
                axios.post(`/registration/${this.user.username}/validate`, data)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        this.form.onFail(error.response.data)
                        eventBus.$emit('stop-processing-payment-dialog')
                        this.addClearErrorEventListener()
                    })
            })
        },

        addClearErrorEventListener() {
            this.$nextTick(() => {
                $('.has-error').find(':input').each((index, input) => {
                    let listener = input.addEventListener('change', event => {
                        this.form.errors.clear(event.target.name)
                        input.removeEventListener('change', listener)
                    })
                })
            })
        },

        // Save the application
        submitRegistration() {
            // this.form.errors.clear()
            this.form.asFormData().post(`/registration/${this.user.username}`)
                .then(response => {
                    this.removeAbandonedListener()
                    this.redirectTo(`/home?welcome`)
                })
                .catch(errors => {
                    eventBus.$emit('stop-processing-payment-dialog')
                    this.newStripeIntent()
                    this.form.errors.add("stripe_token", "A unexpected error has occured, please try again later.");
                })
        },

        setPaymentOption(option){
            this.paymentOptionSelected = option
        }
    },

    computed: {
        // Check if all required field are completed on the client side for the step 1.
        stepOneDisabled() {
            if (this.form.username.trim() == '') return true;
            if (this.form.password.trim() == '') return true;
            if (this.form.password_confirmation.trim() == '') return true;
            return false
        },

        // Check if all required field are completed on the client side for the step 2.
        stepTwoDisabled() {
            if (this.form.profile_picture == '') return true;
            if (this.form.phone_number != "" && !this.isValidPhoneNumber) return true;
            return false
        },

        // Check if all required fields are completed on the client side for the step 3.
        stepThreeDisabled() {
            if (!this.stripeCardCompleted) return true;
            if (this.form.isSubmitted) return true;
            if (this.stripeSubmitted) return true;
            return false
        },
    },

}
</script>
